import WashdayClientSingleton from './WashdayClientSingleton';
import type {
    FinancialSummaryParams,
    DiscountCodeReportDateRangeParams,
    DiscountCodeReportExportParams,
    OrderHistoryReportExportParams,
    OrderHistoryReportParams,
} from 'washday-sdk';
import type {
    OutsourcedOrdersDateField,
    OutsourcedOrdersSortBy,
    OutsourcedOrdersSortDirection,
    OutsourcedStatus,
} from '../hooks/outsourcingOrders/interfaces';

export const getFinancialSummary = (params: FinancialSummaryParams) =>
    WashdayClientSingleton.getInstance().reports.getFinancialSummary(params);

export const exportUnpaidOrdersReportPDF = async (storeId: string, params: {
    store: string | undefined,
    fromDate: string | undefined,
    toDate: string | undefined,
    timezone: string | undefined,
    customerName: string | undefined,
}) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );

        return await WashdayClientSingleton.getInstance().pdf.exportUnpaidOrdersReportPDF(storeId, filteredParams);
    } catch (e) {
        console.log('ERROR exportUnpaidOrdersReportPDF', e);
        throw e;
    }
};

export const getCleanedOrdersReport = async (storeId: string, params: {
    fromDate?: string
    toDate?: string
    timezone?: string
    pageNum?: string
    limit?: string
}) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );

        return await WashdayClientSingleton.getInstance().reports.getCleanedOrdersReport(storeId, filteredParams);
    } catch (e) {
        console.log('ERROR getCleanedOrdersReport', e);
        throw e;
    }
};


export const getCleanedOrdersGraphDataReport = async (storeId: string, params: {
    fromDate?: string
    toDate?: string
    timezone?: string
}) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );

        return await WashdayClientSingleton.getInstance().reports.getCleanedOrdersGraphDataReport(storeId, filteredParams);
    } catch (e) {
        console.log('ERROR getCleanedOrdersGraphDataReport', e);
        throw e;
    }
};

export const exportCleanedOrdersReport = async (storeId: string, params: {
    fromDate: string
    toDate: string
    timezone?: string
}) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );

        return await WashdayClientSingleton.getInstance().csv.exportCleanedOrdersReport(storeId, filteredParams);
    } catch (e) {
        console.log('ERROR exportCleanedOrdersReport', e);
        throw e;
    }
};

export const getSalesReport = async (storeId: string, params: {
    fromDate?: string
    toDate?: string
    timezone?: string,
    type: "daily" | "month" | "year"
}) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );

        return await WashdayClientSingleton.getInstance().reports.getSalesReport(storeId, filteredParams);
    } catch (e) {
        console.log('ERROR getSalesReport', e);
        throw e;
    }
};

export const exportSalesReport = async (storeId: string, params: {
    fromDate: string
    toDate: string
    timezone?: string
    type: "daily" | "month" | "year"
}) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );

        return await WashdayClientSingleton.getInstance().csv.exportSalesReport(storeId, filteredParams);
    } catch (e) {
        console.log('ERROR exportSalesReport', e);
        throw e;
    }
};

export const getDiscountCodesReport = async (
    storeId: string,
    params: DiscountCodeReportDateRangeParams,
) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );

        return await WashdayClientSingleton.getInstance().reports.getDiscountCodesReport(storeId, filteredParams);
    } catch (e) {
        console.log('ERROR getDiscountCodesReport', e);
        throw e;
    }
};

export const getStaffReport = async (storeId: string, params: {
    fromDate?: string
    toDate?: string
}) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );

        return await WashdayClientSingleton.getInstance().reports.getStaffReport(storeId, filteredParams);
    } catch (e) {
        console.log('ERROR getStaffReport', e);
        throw e;
    }
};

export const getSuppliesReport = async (storeId: string, params: {
    fromDate?: string
    toDate?: string
}) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );

        return await WashdayClientSingleton.getInstance().reports.getSuppliesReport(storeId, filteredParams);
    } catch (e) {
        console.log('ERROR getSuppliesReport', e);
        throw e;
    }
};

export const getSupplyCostReport = async (params: {
    store?: string;
    fromDate?: string;
    toDate?: string;
    supplyId?: string;
    productId?: string;
}) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );

        return await WashdayClientSingleton.getInstance().reports.getSupplyCostReport(filteredParams);
    } catch (e) {
        console.log('ERROR getSupplyCostReport', e);
        throw e;
    }
};

export const exportSupplyCostReport = async (params: {
    store?: string;
    fromDate?: string;
    toDate?: string;
    supplyId?: string;
    productId?: string;
}) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );

        return await WashdayClientSingleton.getInstance().reports.exportSupplyCostReport(filteredParams);
    } catch (e) {
        console.log('ERROR exportSupplyCostReport', e);
        throw e;
    }
};

export const getCustomersReport = async (storeId: string, params: {
    fromDate?: string
    toDate?: string
    sortBy?: 'lastOrderDate' | 'ordersQty' | 'ordersSalesTotal'
    sortOrder?: 'asc' | 'desc'
}) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );

        return await WashdayClientSingleton.getInstance().reports.getCustomersReport(storeId, filteredParams);
    } catch (e) {
        console.log('ERROR getCustomersReport', e);
        throw e;
    }
};

export const exportDiscountCodesReport = async (
    storeId: string,
    params: DiscountCodeReportExportParams,
) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );

        return await WashdayClientSingleton.getInstance().csv.exportDiscountCodesReport(storeId, filteredParams);
    } catch (e) {
        console.log('ERROR exportDiscountCodesReport', e);
        throw e;
    }
};

export const exportStaffReport = async (storeId: string, params: {
    fromDate: string
    toDate: string
}) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );

        return await WashdayClientSingleton.getInstance().csv.exportStaffReport(storeId, filteredParams);
    } catch (e) {
        console.log('ERROR exportStaffReport', e);
        throw e;
    }
};

export const exportSuppliesReport = async (storeId: string, params: {
    fromDate: string
    toDate: string
}) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );

        return await WashdayClientSingleton.getInstance().csv.exportSuppliesReport(storeId, filteredParams);
    } catch (e) {
        console.log('ERROR exportSuppliesReport', e);
        throw e;
    }
};

export const exportCustomersReport = async (storeId: string, params: {
    fromDate: string
    toDate: string
    sortBy?: 'lastOrderDate' | 'ordersQty' | 'ordersSalesTotal'
    sortOrder?: 'asc' | 'desc'
}) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );

        return await WashdayClientSingleton.getInstance().csv.exportCustomersReport(storeId, filteredParams);
    } catch (e) {
        console.log('ERROR exportCustomersReport', e);
        throw e;
    }
};

export const getProductSalesReport = async (storeId: string, params: {
    fromDate?: string
    toDate?: string
    timezone?: string
    groupBySection?: boolean
}) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );

        return await WashdayClientSingleton.getInstance().reports.getProductSalesReport(storeId, filteredParams);
    } catch (e) {
        console.log('ERROR getProductSalesReport', e);
        throw e;
    }
};

export const getPaymentLinesReport = async (storeId: string, params: {
    fromDate?: string
    toDate?: string
    timezone?: string
    pageNum?: string
    limit?: string
}) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );

        return await WashdayClientSingleton.getInstance().reports.getPaymentLinesReport(storeId, filteredParams);
    } catch (e) {
        console.log('ERROR getPaymentLinesReport', e);
        throw e;
    }
};

export const getCashierBoxMovementsReport = async (storeId: string, params: {
    cashierBoxId?: string
    fromDate?: string
    toDate?: string
    timezone?: string
    pageNum?: string
    limit?: string
}) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );

        return await WashdayClientSingleton.getInstance().reports.getCashierBoxMovementsReport(storeId, filteredParams);
    } catch (e) {
        console.log('ERROR getCashierBoxMovementsReport', e);
        throw e;
    }
};

export const getUnpaidOrdersReport = async (storeId: string, params: {
    fromDate?: string
    toDate?: string
    timezone?: string
    pageNum?: string
    limit?: string
    customerName?: string
}) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );

        return await WashdayClientSingleton.getInstance().reports.getUnpaidOrdersReport(storeId, filteredParams);
    } catch (e) {
        console.log('ERROR getUnpaidOrdersReport', e);
        throw e;
    }
};

export const getStaffActivityDetails = async (storeId: string, params: {
    fromDate?: string
    toDate?: string
    employeeId?: string
}) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );
        return await WashdayClientSingleton.getInstance().reports.getStaffActivityDetails(storeId, filteredParams);
    } catch (e) {
        console.log('ERROR getStaffActivityDetails', e);
        throw e;
    }
};

export const getProductSalesDrillDown = async (storeId: string, params: {
    storeProductId: string
    fromDate?: string
    toDate?: string
    timezone?: string
}) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );

        return await WashdayClientSingleton.getInstance().reports.getProductSalesDrillDown(storeId, filteredParams as any);
    } catch (e) {
        console.log('ERROR getProductSalesDrillDown', e);
        throw e;
    }
};

export const exportProductSalesReport = async (storeId: string, params: {
    fromDate?: string
    toDate?: string
    timezone?: string
    groupBySection?: boolean
}) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );

        return await WashdayClientSingleton.getInstance().csv.exportProductSalesReport(storeId, filteredParams);
    } catch (e) {
        console.log('ERROR exportProductSalesReport', e);
        throw e;
    }
};

export const exportPaymentLinesReport = async (storeId: string, params: {
    fromDate?: string
    toDate?: string
    timezone?: string
}) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );

        return await WashdayClientSingleton.getInstance().csv.exportPaymentLinesReport(storeId, filteredParams);
    } catch (e) {
        console.log('ERROR exportPaymentLinesReport', e);
        throw e;
    }
};

export const exportOutsourcingReport = async (storeId: string, params: {
    statuses?: OutsourcedStatus[]
    sequence?: string
    partnerId?: string
    dateFrom?: string
    dateTo?: string
    dateField?: OutsourcedOrdersDateField
    sortBy?: OutsourcedOrdersSortBy
    sortDirection?: OutsourcedOrdersSortDirection
    timezone?: string
}) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );

        return await WashdayClientSingleton.getInstance().csv.exportOutsourcingReport(storeId, filteredParams);
    } catch (e) {
        console.log('ERROR exportOutsourcingReport', e);
        throw e;
    }
};

export const exportCashierBoxMovementsReport = async (storeId: string, params: {
    cashierBoxId?: string
    fromDate?: string
    toDate?: string
    timezone?: string
}) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );

        return await WashdayClientSingleton.getInstance().csv.exportCashierBoxMovementsReport(storeId, filteredParams);
    } catch (e) {
        console.log('ERROR exportCashierBoxMovementsReport', e);
        throw e;
    }
};

export const exportUnpaidOrdersReportCSV = async (storeId: string, params: {
    fromDate?: string
    toDate?: string
    timezone?: string
    customerName?: string
}) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );

        return await WashdayClientSingleton.getInstance().csv.exportUnpaidOrdersReportCSV(storeId, filteredParams);
    } catch (e) {
        console.log('ERROR exportUnpaidOrdersReportCSV', e);
        throw e;
    }
};

export const getAnnualSalesStatistics = async (storeId: string, params: {
    fromDate?: string
    toDate?: string
    timezone?: string
}) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );

        return await WashdayClientSingleton.getInstance().reports.getAnnualSalesStatistics(storeId, filteredParams);
    } catch (e) {
        console.log('ERROR getAnnualSalesStatistics', e);
        throw e;
    }
};

export const getPaymentMethodStatistics = async (storeId: string, params: {
    fromDate?: string
    toDate?: string
    timezone?: string
}) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );

        return await WashdayClientSingleton.getInstance().reports.getPaymentMethodStatistics(storeId, filteredParams);
    } catch (e) {
        console.log('ERROR getPaymentMethodStatistics', e);
        throw e;
    }
};

export const getProductsStatistics = async (storeId: string, params: {
    fromDate?: string
    toDate?: string
    timezone?: string
}) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );

        return await WashdayClientSingleton.getInstance().reports.getProductsStatistics(storeId, filteredParams);
    } catch (e) {
        console.log('ERROR getProductsStatistics', e);
        throw e;
    }
};

export const getMonthSalesStatistics = async (storeId: string, params: {
    fromDate?: string
    toDate?: string
    timezone?: string
}) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );

        return await WashdayClientSingleton.getInstance().reports.getMonthSalesStatistics(storeId, filteredParams);
    } catch (e) {
        console.log('ERROR getMonthSalesStatistics', e);
        throw e;
    }
};

export const getPopularDaysStatistics = async (storeId: string, params: {
    fromDate?: string
    toDate?: string
    timezone?: string
}) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );

        return await WashdayClientSingleton.getInstance().reports.getPopularDaysStatistics(storeId, filteredParams);
    } catch (e) {
        console.log('ERROR getPopularDaysStatistics', e);
        throw e;
    }
};

export const getAttendanceReport = async (storeId: string, params: {
    fromDate?: string
    toDate?: string
    employeeId?: string | null
    pageNum?: number
    limit?: number
}) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined && value !== null && value !== '')
        );

        // Map fromDate/toDate to startDate/endDate for the backend
        const mappedParams: any = {};
        if (filteredParams.fromDate) mappedParams.startDate = filteredParams.fromDate;
        if (filteredParams.toDate) mappedParams.endDate = filteredParams.toDate;
        if (filteredParams.employeeId) mappedParams.employeeId = filteredParams.employeeId;
        if (filteredParams.pageNum) mappedParams.pageNum = filteredParams.pageNum;
        if (filteredParams.limit) mappedParams.limit = filteredParams.limit;

        return await WashdayClientSingleton.getInstance().attendance.getStoreReport(storeId, mappedParams);
    } catch (e) {
        console.log('ERROR getAttendanceReport', e);
        throw e;
    }
};

export const exportAttendanceReport = async (storeId: string, params: {
    fromDate?: string
    toDate?: string
    employeeId?: string | null
}) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined && value !== null && value !== '')
        );

        // Map fromDate/toDate to startDate/endDate for the backend
        const mappedParams: any = {};
        if (filteredParams.fromDate) mappedParams.startDate = filteredParams.fromDate;
        if (filteredParams.toDate) mappedParams.endDate = filteredParams.toDate;
        if (filteredParams.employeeId) mappedParams.employeeId = filteredParams.employeeId;

        return await WashdayClientSingleton.getInstance().attendance.exportStoreReport(storeId, mappedParams);
    } catch (e) {
        console.log('ERROR exportAttendanceReport', e);
        throw e;
    }
};

export const getCashUpReportsByDateRange = async (storeId: string, params: {
    fromDate?: string
    toDate?: string
    cashierBoxId?: string
}) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );
        return await WashdayClientSingleton.getInstance().reports.getCashUpReportsByDateRange(storeId, filteredParams);
    } catch (e) {
        console.log('ERROR getCashUpReportsByDateRange', e);
        throw e;
    }
};

export const exportCashUpReportsByDateRange = async (storeId: string, params: {
    fromDate?: string
    toDate?: string
    cashierBoxId?: string
}) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );
        return await WashdayClientSingleton.getInstance().csv.exportCashUpReportsByDateRange(storeId, filteredParams);
    } catch (e) {
        console.log('ERROR exportCashUpReportsByDateRange', e);
        throw e;
    }
};


export const getScheduledDeliveries = async (companyId: string, params: {
    storeId?: string
    dateFrom?: string
    dateTo?: string
    includeDelivered?: string
}) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );
        return await WashdayClientSingleton.getInstance().reports.getScheduledDeliveries(companyId, filteredParams);
    } catch (e) {
        console.log('ERROR getScheduledDeliveries', e);
        throw e;
    }
};

export const exportScheduledDeliveriesReportCSV = async (companyId: string, params: {
    storeId?: string
    dateFrom?: string
    dateTo?: string
    includeDelivered?: string
}) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );
        return await WashdayClientSingleton.getInstance().csv.exportScheduledDeliveriesReport(companyId, filteredParams);
    } catch (e) {
        console.log('ERROR exportScheduledDeliveriesReport', e);
        throw e;
    }
};

export type ServicesToDeliverFilters = {
    storeId?: string
    dateFrom?: string
    dateTo?: string
    hideReady?: string
};

export const getServicesToDeliverReport = async (companyId: string, params: ServicesToDeliverFilters) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );
        return await WashdayClientSingleton.getInstance().reports.getServicesToDeliver(companyId, filteredParams);
    } catch (e) {
        console.log('ERROR getServicesToDeliverReport', e);
        throw e;
    }
};

export const exportServicesToDeliverReportCSV = async (companyId: string, params: ServicesToDeliverFilters) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== undefined)
        );
        return await WashdayClientSingleton.getInstance().csv.exportServicesToDeliverReport(companyId, filteredParams);
    } catch (e) {
        console.log('ERROR exportServicesToDeliverReport', e);
        throw e;
    }
};

export const getOrdersHistoryReport = async (
    companyId: string,
    params: OrderHistoryReportParams,
) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter((entry) => entry[1] !== undefined)
        ) as OrderHistoryReportParams;
        return await WashdayClientSingleton.getInstance().reports.getOrdersHistoryReport(
            companyId,
            filteredParams,
        );
    } catch (e) {
        console.log('ERROR getOrdersHistoryReport', e);
        throw e;
    }
};

export const exportOrdersHistoryReport = async (
    companyId: string,
    params: OrderHistoryReportExportParams,
) => {
    try {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter((entry) => entry[1] !== undefined)
        ) as OrderHistoryReportExportParams;
        return await WashdayClientSingleton.getInstance().csv.exportOrdersHistoryReport(
            companyId,
            filteredParams,
        );
    } catch (e) {
        console.log('ERROR exportOrdersHistoryReport', e);
        throw e;
    }
};
